import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

// Root-level error boundary. Without this, any render-time throw in a page
// unmounts the whole tree and leaves a blank screen. This catches it and
// offers a recovery path instead.
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console (and, once wired, an error reporter like Sentry).
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-makhe-bg px-6 text-center">
        <div className="max-w-md">
          <p className="font-display text-7xl text-makhe-primary">oops</p>
          <h1 className="mt-4 font-display text-3xl">Something broke on our side.</h1>
          <p className="mt-3 text-sm text-makhe-muted">
            This page hit an unexpected error. Reloading usually fixes it. If it keeps
            happening, let us know.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-full bg-makhe-ink px-6 py-3 text-[11px] uppercase tracking-widest text-makhe-bg"
            >
              Reload
            </button>
            <a
              href="/feed"
              className="rounded-full border border-makhe-ink px-6 py-3 text-[11px] uppercase tracking-widest text-makhe-ink"
            >
              Back to feed
            </a>
          </div>
        </div>
      </div>
    );
  }
}
